import {
  isMilestoneInfraImpact,
  type InfraImpact,
  type SignalCategoryType,
} from '@terra/shared-schemas-orchestrator';
import {
  resolveWeaknessCatalogEntry,
  type InternalNetworkAttackPathEventDto,
  type InternalNetworkAttackPathTimelineDto,
} from '@terra/shared-contracts-internal-network';

export type AttackPathEventHopInput = {
  hopIndex: number;
  targetHost: string;
  technique?: string | null;
  command?: string | null;
  category?: string | null;
  edgeType?: string | null;
  prerequisiteCredential?: string | null;
  isDomainController?: boolean;
  isCrownJewel?: boolean;
};

const eventBase = ({
  id,
  hopIndex,
  occurredAt,
  sourceEventIds,
  technique,
  mitreTechniqueId,
  exploitsWeakness = false,
}: {
  id: string;
  hopIndex: number;
  occurredAt: string;
  sourceEventIds: string[];
  technique: string | null;
  mitreTechniqueId: string | null;
  exploitsWeakness?: boolean;
}) => ({
  id,
  networkId: id,
  hopIndex,
  occurredAt,
  status: 'SUCCEEDED' as const,
  sourceEventIds,
  operationId: null,
  operationTaskId: null,
  evidenceId: null,
  mitreTechniqueId,
  technique,
  exploitsWeakness,
});

export const buildAttackPathEventTimelineFromHops = ({
  id,
  createdAt,
  hops,
}: {
  id: string;
  createdAt: string;
  hops: AttackPathEventHopInput[];
}): InternalNetworkAttackPathTimelineDto => {
  const events: InternalNetworkAttackPathEventDto[] = [];
  let previousNodeId: string | null = null;
  let previousHostname: string | null = null;

  hops.forEach((hop) => {
    const entry = resolveWeaknessCatalogEntry({
      category: hop.category,
      command: hop.command ?? hop.technique,
      edgeType: hop.edgeType,
      technique: hop.technique,
    });

    const hostId = `host-${id}-${hop.hopIndex}`;

    events.push({
      ...eventBase({
        id: hostId,
        hopIndex: hop.hopIndex,
        occurredAt: createdAt,
        sourceEventIds: previousNodeId ? [previousNodeId] : [],
        technique: null,
        mitreTechniqueId: null,
        exploitsWeakness: false,
      }),
      kind: 'HOST_DISCOVERED',
      hostname: hop.targetHost,
      assetId: null,
      adEntityId: null,
      isDomainController: hop.isDomainController ?? false,
      isCrownJewel: hop.isCrownJewel ?? false,
      operatingSystem: null,
    });
    previousNodeId = hostId;

    if (entry) {
      const weaknessNodeId = `weakness-${id}-${hop.hopIndex}`;

      events.push({
        ...eventBase({
          id: weaknessNodeId,
          hopIndex: hop.hopIndex,
          occurredAt: createdAt,
          sourceEventIds: [previousNodeId],
          technique: hop.technique ?? null,
          mitreTechniqueId: entry.mitreTechniqueId,
          exploitsWeakness: true,
        }),
        kind: 'WEAKNESS_EXPLOITED',
        weaknessId: entry.weaknessId,
        title: entry.shortName,
        findingId: null,
        category: entry.category as SignalCategoryType | null,
        severity: null,
        hostname: hop.targetHost,
        sourceHostname: previousHostname,
        isProven: false,
      });
      previousNodeId = weaknessNodeId;

      if (entry.credentialYield) {
        const credentialNodeId = `cred-${id}-${hop.hopIndex}`;

        events.push({
          ...eventBase({
            id: credentialNodeId,
            hopIndex: hop.hopIndex,
            occurredAt: createdAt,
            sourceEventIds: [previousNodeId],
            technique: null,
            mitreTechniqueId: null,
            exploitsWeakness: true,
          }),
          kind: 'CREDENTIAL_OBTAINED',
          label: hop.prerequisiteCredential ?? entry.credentialYield.label,
          provenance: entry.credentialYield.provenance,
          privilegeTier: entry.credentialYield.privilegeTier,
          username: null,
          domain: null,
          credentialId: null,
          hostname: hop.targetHost,
        });
        previousNodeId = credentialNodeId;
      }

      if (
        entry.infraImpact &&
        isMilestoneInfraImpact(entry.infraImpact as InfraImpact)
      ) {
        const impactNodeId = `impact-${id}-${hop.hopIndex}`;

        events.push({
          ...eventBase({
            id: impactNodeId,
            hopIndex: hop.hopIndex,
            occurredAt: createdAt,
            sourceEventIds: [previousNodeId],
            technique: null,
            mitreTechniqueId: null,
            exploitsWeakness: true,
          }),
          kind: 'IMPACT_ACHIEVED',
          infraImpact: entry.infraImpact as InfraImpact,
          findingId: null,
          hostname: hop.targetHost,
          sourceHostname: previousHostname,
        });
        previousNodeId = impactNodeId;
      }
    }

    previousHostname = hop.targetHost;
  });

  return {
    assessmentId: null,
    networkId: id,
    startedAt: createdAt,
    completedAt: null,
    events,
    origin: 'DERIVED',
  };
};
